import React, { useState, useCallback } from 'react';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Cropper from 'react-easy-crop';
import Skeleton from '@material-ui/lab/Skeleton';
import PropTypes from 'prop-types';
import {
    isFirefox,
    isSafari,
    isChrome,
    isEdge,
    isAndroid,
    isIOS,
    isMobile,
} from 'react-device-detect';

const safari = '/resources/images/safari_th.png';
const chrome = '/resources/images/chrome_th.png';
const edge = '/resources/images/edge_th.png';
const firefox = '/resources/images/firefox_th.png';
const safari_mobile = '/resources/images/safari_mobile_th.png';
const chrome_mobile = '/resources/images/chrome_mobile_th.png';

import getCroppedImg from '../../utils/cropImage';

import {
    ContainedButton,
    OutlinedButton,
    Avatar,
    GridContainer,
    GridItem,
    Text,
    Span,
    FilledIconButton,
    MicIcon,
    MicOffIcon,
    VideoIcon,
    VideoOffIcon,
    PhoneEndIcon,
    PhoneIcon,
    Subtitle,
    CalendarIcon,
    TextButton,
    RadioGroup,
    Radio,
    CheckboxGroup,
    FormGroup,
    Checkbox,
} from '../index';

import {
    StyledDialog,
    StyledPaper,
    StyledDialogContent,
    StyledDialogActions,
    StyledRingingDialog,
    StyledPDPAPaper,
    StyledAccptButton,
    StyledPaperPermission,
} from './style';
import { ClosePermission } from '../Icon';

const noCallImg = '/resources/images/nocall.svg';

export const ConfirmDialog = ({
    open,
    onCancel,
    id,
    description,
    title,
    contentText,
    confirmText,
    cancelText,
    onConfirm,
    confirmBackgroundColor,
    contentTextColor,
    loading,
    ...rest
}) => {
    return (
        <StyledDialog
            {...rest}
            open={open}
            onClose={onCancel}
            aria-labelledby={`${id}-dialog`}
            aria-describedby={description}
            PaperComponent={StyledPaper}
        >
            {title && (
                <Text $fontSize="18px" $bold style={{ marginBottom: '10px' }}>
                    {title}
                </Text>
            )}
            <StyledDialogContent>
                <Text $color={contentTextColor ? contentTextColor : 'black'}>{contentText}</Text>
            </StyledDialogContent>

            <StyledDialogActions>
                {onCancel && cancelText && (
                    <OutlinedButton
                        onClick={onCancel}
                        $width="120px"
                        $height="32px"
                        $color="pink"
                        disabled={loading}
                    >
                        {cancelText}
                    </OutlinedButton>
                )}
                {onConfirm && confirmText && (
                    <ContainedButton
                        onClick={onConfirm}
                        $width="auto"
                        $minWidth="120px"
                        $height="32px"
                        $backgroundColor={confirmBackgroundColor}
                        loading={loading}
                    >
                        {confirmText}
                    </ContainedButton>
                )}
            </StyledDialogActions>
        </StyledDialog>
    );
};

ConfirmDialog.propTypes = {
    open: PropTypes.bool,
    onCancel: PropTypes.func,
    id: PropTypes.string,
    description: PropTypes.string,
    title: PropTypes.string,
    contentText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    onConfirm: PropTypes.func,
    confirmBackgroundColor: PropTypes.string,
    contentTextColor: PropTypes.string,
    rest: PropTypes.object,
};

export const RingingDialog = ({
    open,
    onClose,
    $desktopLayout,
    data,
    onAnswer,
    $providerLayout,
    onSkip,
    micOn,
    handleMic,
    cameraOn,
    handleCamera,
    disabledOnAnswer,
}) => {
    return (
        <StyledRingingDialog
            open={open}
            onClose={onClose}
            PaperProps={{
                style: {
                    borderRadius: '16px',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    height: '100%',
                    maxWidth: 'none',
                    width: '100%',
                    maxHeight: '100%',
                    padding: '20px 20px 25px 20px',
                    margin: 0,
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
            }}
            BackdropProps={{
                style: {
                    borderRadius: $desktopLayout ? '16px' : 0,
                },
            }}
            $desktopLayout={$desktopLayout}
            $providerLayout={$providerLayout}
        >
            {$providerLayout && <StyledDialog open={true} />}
            {onSkip && (
                <div style={{ position: 'absolute', right: '30px', top: '30px' }}>
                    <TextButton $color="white" onClick={onSkip}>
                        Skip this queue
                    </TextButton>
                </div>
            )}
            <GridContainer style={{ height: '100%', maxWidth: '300px' }}>
                <GridItem margin="20px 0 0 0">
                    <div className="ripple-container">
                        <Avatar
                            src={data?.user?.avatar?.url}
                            alt={data?.user?.fullname}
                            $avatarSize="100px"
                            $borderRadius="50%"
                        />
                    </div>
                </GridItem>
                <GridItem>
                    <Text $color="white" $fontSize="14px">
                        Ringing
                    </Text>
                </GridItem>
                <GridItem>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                    >
                        <Text $color="white" $fontSize="24px">{`${data?.user?.fullname}`}</Text>
                        <Text $color="white" style={{ marginTop: '10px', textAlign: 'center' }}>
                            {data?.staff_position?.name}
                        </Text>
                    </div>
                </GridItem>
                <GridContainer>
                    <GridItem xs={6} md={6}>
                        <FilledIconButton
                            $width="48px"
                            $height="48px"
                            onClick={() => handleMic(!micOn)}
                            $backgroundColor={micOn ? 'primaryBlue' : 'gray7'}
                        >
                            {micOn ? <MicIcon /> : <MicOffIcon fill="purple2" />}
                        </FilledIconButton>
                    </GridItem>
                    <GridItem xs={6} md={6}>
                        <FilledIconButton
                            $width="48px"
                            $height="48px"
                            onClick={() => handleCamera(!cameraOn)}
                            $backgroundColor={cameraOn ? 'primaryBlue' : 'gray7'}
                        >
                            {cameraOn ? <VideoIcon /> : <VideoOffIcon fill="purple2" />}
                        </FilledIconButton>
                    </GridItem>
                </GridContainer>
                <GridContainer justifyContent="space-between">
                    <GridItem xs={6} md={6} justifyContent="flex-start">
                        <FilledIconButton
                            $width="64px"
                            $height="64px"
                            onClick={onClose}
                            $backgroundColor="pink"
                        >
                            <PhoneEndIcon />
                        </FilledIconButton>
                    </GridItem>
                    <GridItem xs={6} md={6} justifyContent="flex-end">
                        <FilledIconButton
                            $width="64px"
                            $height="64px"
                            onClick={onAnswer}
                            $backgroundColor="green"
                            disabled={!onAnswer || disabledOnAnswer}
                        >
                            <PhoneIcon />
                        </FilledIconButton>
                    </GridItem>
                </GridContainer>
            </GridContainer>
        </StyledRingingDialog>
    );
};

RingingDialog.propTypes = {
    data: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    $desktopLayout: PropTypes.bool,
    onAnswer: PropTypes.func,
    disabledOnAnswer: PropTypes.bool,
    $providerLayout: PropTypes.bool,
    onSkip: PropTypes.func,
    micOn: PropTypes.bool,
    handleMic: PropTypes.func,
    cameraOn: PropTypes.bool,
    handleCamera: PropTypes.func,
};

export const UploadImageDialog = ({ image, onCropImage, onClose, open }) => {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    // const [croppedImage, setCroppedImage] = useState(null)

    // eslint-disable-next-line
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const showCroppedImage = useCallback(async () => {
        try {
            const croppedImage = await getCroppedImg(image, croppedAreaPixels);
            // console.log('croppedAreaPixels', croppedAreaPixels);
            // console.log('croppedImage', { croppedImage });
            // setCroppedImage(croppedImage)
            onCropImage(croppedImage);
            onClose();
        } catch (e) {
            console.error(e);
        }
    }, [croppedAreaPixels]);

    return (
        <StyledDialog open={open} onClose={onClose}>
            <StyledDialogContent>
                <div
                    style={{
                        position: 'relative',
                        width: mdUp ? '600px' : '300px',
                        height: mdUp ? '400px' : '250px',
                        background: '#333',
                    }}
                >
                    <Cropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        aspect={1 / 1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                    />
                </div>
                <div
                    style={{
                        backgroundColor: '#191919',
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '10px',
                    }}
                >
                    <ContainedButton onClick={showCroppedImage} $width="120px">
                        Save
                    </ContainedButton>
                </div>
            </StyledDialogContent>
        </StyledDialog>
    );
};

UploadImageDialog.propTypes = {
    open: PropTypes.bool,
    image: PropTypes.string,
    onCropImage: PropTypes.func,
    onClose: PropTypes.func,
};

const skipQueueRemark = [
    { value: '0', label: 'Can’t contact the client.' },
    { value: '1', label: 'Client contacted me to cancel queue.' },
];

export const SkipQueueDialog = ({ open, onCancel, onConfirm }) => {
    const [selectedRemark, setSelectedRemark] = useState('0');

    const handleChange = (e) => {
        setSelectedRemark(e.target.value);
    };

    const onClickSkip = () => {
        onConfirm(skipQueueRemark[selectedRemark].label);
    };

    return (
        <StyledDialog
            open={open}
            onClose={onCancel}
            PaperComponent={StyledPaper}
            style={{ zIndex: 1302 }}
        >
            <StyledDialogContent>
                <Text $color="black" $bold>
                    Are you sure you want to skip this queue ?
                </Text>
                <Subtitle style={{ marginTop: '10px' }}>
                    System will send message to the client after your confirmmation.
                </Subtitle>
                <Text style={{ marginTop: '10px' }}>Why did you skip this queue ?</Text>
                <RadioGroup name="sessionType" value={selectedRemark} onChange={handleChange}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {skipQueueRemark.map((r) => (
                            <Radio
                                key={`skipQueueRemark-${r.value}`}
                                value={r.value}
                                label={r.label}
                            />
                        ))}
                    </div>
                </RadioGroup>
            </StyledDialogContent>
            <StyledDialogActions>
                <ContainedButton
                    onClick={onClickSkip}
                    $width="120px"
                    $height="32px"
                    $backgroundColor="pink"
                    disabled={!selectedRemark}
                >
                    Skip
                </ContainedButton>
                <OutlinedButton onClick={onCancel} $width="120px" $height="32px">
                    Cancel
                </OutlinedButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

SkipQueueDialog.propTypes = {
    open: PropTypes.bool,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
};

export const SessionDetailDialog = ({ open, onClose, data, onEdit }) => {
    return (
        <StyledDialog
            scroll="body"
            open={open}
            onClose={onClose}
            aria-labelledby="SessionDetail"
            aria-describedby="Session Detail"
            PaperComponent={StyledPaper}
        >
            <div
                style={{
                    minWidth: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <Text>Session Detail</Text>
                {data ? (
                    <Text $fontSize="14px" $bold>
                        {data?.title}
                    </Text>
                ) : (
                    <Skeleton width={200} height={30} variant="text" />
                )}
                {data ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '5px',
                        }}
                    >
                        <CalendarIcon fill="pink" width="16px" height="16px" />
                        <Text $color="pink" $fontSize="12px" $bold>
                            {data?.date}
                        </Text>
                    </div>
                ) : (
                    <Skeleton width={200} height={30} variant="text" />
                )}
                {data ? (
                    <Text $fontSize="12px" $color="gray2">
                        {data?.description}
                    </Text>
                ) : (
                    <Skeleton width={250} height={60} variant="text" />
                )}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        columnGap: '5px',
                    }}
                >
                    <OutlinedButton
                        onClick={onEdit}
                        disabled={!data}
                        style={{ width: '80px', height: '32px' }}
                    >
                        Edit
                    </OutlinedButton>
                    <ContainedButton
                        onClick={onClose}
                        disabled={!data}
                        style={{ width: '96px', height: '32px' }}
                    >
                        Close
                    </ContainedButton>
                </div>
            </div>
        </StyledDialog>
    );
};

SessionDetailDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    data: PropTypes.object,
    onEdit: PropTypes.func,
};

export const NoCallDialog = ({ open, onClose, type }) => {
    let data = {};

    switch (type) {
        case 'REJECT':
            data = {
                title: 'Opps! No acknowledgement from your requested.',
                content:
                    'Sorry. There is no acknowledgement from your requested call. Please try again later.',
            };
            break;
        case 'ANOTHOR_CALL':
            data = {
                title: 'Opps! Client is currently in another call.',
                content:
                    "Sorry, the client that you've dialed is in another call,  Please try again later.",
            };
            break;
        default:
            data = {
                title: 'Opps! cannot be connected at this time.',
                content:
                    "Sorry, the client that you've dialed cannot be connected at this time,  Please try again later.",
            };
    }

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            aria-labelledby={`no-call-dialog`}
            aria-describedby="dialog will be visible when client is offline or hang up"
            PaperComponent={StyledPaper}
            PaperProps={{
                style: {
                    maxWidth: '400px',
                },
            }}
        >
            <StyledDialogContent
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    rowGap: '20px',
                }}
            >
                <Text $bold style={{ textAlign: 'center' }}>
                    {data.title}
                </Text>
                <img src={noCallImg} alt="no call img" style={{ width: '158px' }} />
                <Text $fontSize="12px" style={{ textAlign: 'center' }}>
                    {data.content}
                </Text>
                <OutlinedButton style={{ alignSelf: 'flex-end', width: '80px' }} onClick={onClose}>
                    Close
                </OutlinedButton>
            </StyledDialogContent>
        </StyledDialog>
    );
};

NoCallDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
};

export const HungUpDialog = ({ open, onClose, type }) => {
    let data = {};

    switch (type) {
        case 'CANCEL':
            data = {
                title: 'The call has been disconnected by provider.',
                content: 'Please wait for call back.',
            };
            break;
        default:
            data = {
                title: 'Your queues has been skipped!',
                content: 'Please wait for the next queue.',
            };
    }

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            aria-labelledby={`skip-queue-dialog`}
            aria-describedby="dialog will be visible when queue was skip or provider hung up before client answer"
            PaperComponent={StyledPaper}
            PaperProps={{
                style: {
                    maxWidth: '330px',
                },
            }}
        >
            <StyledDialogContent
                style={{
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    rowGap: '20px',
                }}
            >
                <Text>{data.title}</Text>
                <Text>{data.content}</Text>
                <OutlinedButton style={{ alignSelf: 'flex-end', width: '80px' }} onClick={onClose}>
                    Close
                </OutlinedButton>
            </StyledDialogContent>
        </StyledDialog>
    );
};

HungUpDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
};

export const PDPADialog = ({ open, onClose, accepted, onCheckChange, onAccept }) => {
    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            aria-labelledby="PDPA-dialog"
            aria-describedby="PDPA-dialog"
            PaperComponent={StyledPDPAPaper}
        >
            <Text $bold style={{ marginBottom: '25px' }}>
                นโยบายคุ้มครองข้อมูลส่วนบุคคลของบริษัท วีคเดย์ จำกัด
            </Text>
            <Text $fontSize="14px" $color="gray1">
                นโยบายคุ้มครองข้อมูลส่วนบุคคลของบริษัท วีคเดย์ จำกัดบริษัท วีคเดย์ จำกัด
                จะดำเนินการคุ้มครองข้อมูล ส่วนบุคคลของท่านตามกฎหมาย
                และมีแนวทางดำเนินการเกี่ยวกับข้อมูลส่วนบุคคลของท่านตามนโยบายคุ้มครอง
                ข้อมูลส่วนบุคคลฉบับนี้ ซึ่งสอดคล้องเป็นไปตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล
                พ.ศ. 2562 ตามที่บริษัท วีคเดย์ จำกัด ได้แจ้ง
                เรื่องการให้ความยินยอมเปิดเผยข้อมูลส่วนบุคคลของท่านในเว็บไซต์
                หรือแอพพลิเคชั่นที่บริษัท วีคเดย์ จำกัด ให้บริการ ว่า
                การดำเนินให้ข้อมูลส่วนบุคคลโดยการระบุข้อมูล หรือการทำเครื่องหมายลงแบบฟอร์ม
                เพื่อการติดต่อหรือรับบริการกับบริษัท วีคเดย์ จำกัด ในระหว่างเยี่ยมชมหรือใช้เว็บไซต์
                หรือแอพพลิเคชั่น ที่บริษัท วีคเดย์ จำกัด ให้บริการ
                จะถือว่าท่านได้ยินยอมและตกลงด้วยกับนโยบายคุ้มครองข้อมูลส่วนบุคคลแล้ว
                ดังรายละเอียดต่อไปนี้
                <br />
                <br />
                <Span $bold>1. ข้อมูลที่จัดเก็บรวบรวม</Span>
                <br />
                บริษัท วีคเดย์ จำกัด จะดำเนินการจัดเก็บรวบรวมข้อมูลส่วนบุคคลของท่านเท่าที่จำเป็น
                เพื่อการรับบริการต่าง ๆ กับบริษัท วีคเดย์ จำกัด
                เพื่อให้เกิดประสิทธิภาพและประโยชน์สูงสุดในการเข้ารับบริการรักษากับบริษัทวีคเดย์
                จำกัด เช่น การให้ข้อมูลส่วนบุคคลเพื่อการเข้ารับการรักษา การรับข่าวสาร
                การสอบถามข้อมูลการนัดหมายแพทย์ การรับ บริการระบบแพทย์ทางไกล(Telemedicine)
                การรับบริการปรึกษาแพทย์ออนไลน์(Online Consulting Service) การชำระเงินออนไลน์
                รวมถึงเลขบัญชีธนาคารต่างๆ เป็นต้น
                ทั้งนี้ท่านอาจเลือกที่จะไม่ให้ข้อมูลส่วนบุคคลแก่บริษัท วีคเดย์ จำกัด ก็ได้
                แต่จะมีผลให้วีคเดย์ จำกัด อาจจะไม่สามารถให้บริการได้อย่างครอบคลุมถูกต้องสมบูรณ์
                และเกิดประโยชน์สูงสุดแก่ท่าน
                <br />
                <br />
                <Span $bold>2. การใช้ข้อมูลส่วนบุคคล</Span>
                <br />
                บริษัท วีคเดย์ จำกัด จะดำเนินการใช้ข้อมูลส่วนบุคคลของท่านเท่าที่จำเป็น
                เพื่อการติดต่อหรือให้ บริการท่านเกี่ยวกับ การรักษา
                การประชาสัมพันธ์หรือแจ้งข้อมูลข่าวสารต่างๆแก่ท่าน รวมถึงเรื่องอื่น ๆ
                ที่เกี่ยวข้องกับการบริการต่าง ๆ
                นอกจากนี้อาจใช้เพื่อประโยชน์ด้านการศึกษาวิจัยหรือทางสถิติ
                ตลอดจนเพื่อการพัฒนามาตรฐานบริการ
                <br />
                <br />
                <Span $bold>3. การเปิดเผยข้อมูลส่วนบุคคล</Span>
                <br />
                ข้อมูลส่วนบุคคลของท่านที่บริษัท วีคเดย์ จำกัด ได้ดำเนินการบันทึกจัดเก็บไว้
                อาจมีการเปิดเผยให้แก่บุคคลภายนอก กรณีดังต่อไปนี้
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;3.1 การเปิดเผยหรือส่งต่อไปยังบุคคลภายนอก
                ซึ่งมีส่วนเกี่ยวข้องกับการให้บริการแก่ท่าน ได้แก่ บริษัทประกันภัย
                ซึ่งเป็นผู้ให้บริการคุ้มครองสิทธิค่าบริการรักษาพยาบาลของท่าน
                ธนาคารผู้ให้บริการรับชำระเงินค่าบริการผ่าน ทางออนไลน์
                หรือบริษัทผู้สนับสนุนการให้บริการอื่น ๆ ที่จำเป็นสำหรับการให้บริการของบริษัท วีคเดย์
                จำกัด
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;3.2
                การจ้างบริษัทหรือบุคคลภายนอกเพื่อการวิเคราะห์ข้อมูล บริการจัดการข้อมูล
                พัฒนาเว็บไซต์หรือแอพพลิเคชั่น ตลอดจนการร่วมพัฒนาบริการ และ/หรือ
                ทำการตลาดร่วมกันกับบริษัท วีคเดย์ จำกัด
                โดยบริษัทหรือบุคคลภายนอกที่เกี่ยวข้องดังกล่าวจะได้รับอนุญาตให้เข้าใช้ข้อมูลส่วนบุคคลของท่านที่จำเป็นในการปฏิบัติหน้าที่หรือดำเนินงาน
                และต้องอยู่ภายใต้บังคับสัญญาที่รักษาความลับของข้อมูลและเป็นไปตามข้อกำหนดกฎหมาย
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;3.3
                การส่งต่อข้อมูลส่วนบุคคลของท่านเพื่อประโยชน์โดยชอบด้วยกฎหมายแก่บุคคลภายนอก
                เช่นเจ้าหน้าที่ตำรวจ หรือเพื่อสาธารณประโยชน์ รวมถึงเพื่อการป้องกันการทุจริต
                การโจรกรรมข้อมูล การโจมตีทางไซเบอร์ เพื่อการดำเนินการทางกฎหมาย
                <br />
                <br />
                <Span $bold>4. ระยะเวลาการเก็บรักษาข้อมูลส่วนบุคคล</Span>
                <br />
                ข้อมูลส่วนบุคคลของท่านจะถูกเก็บรักษาไว้นานเท่าที่จำเป็น
                เพื่อใช้ให้เป็นไปตามตามวัตถุประสงค์ ตามข้อ 2. และข้อ 3. ข้างต้น
                เพื่อประโยชน์สูงสุดแก่ท่านในการเข้ารับบริการต่าง ๆ
                <br />
                <br />
                <Span $bold>5. การรักษาความปลอดภัยข้อมูลส่วนบุคคล </Span>
                <br />
                บริษัท วีคเดย์ จำกัด
                จะดำเนินการใช้มาตรการรักษาความปลอดภัยสูงสุดกับข้อมูลส่วนบุคคลของท่าน โดยมีเทคนิค
                ขั้นตอน และการบริหารจัดการที่เหมาะสม เชื่อถือได้
                และปฏิบัติตามระเบียบที่กำหนดไว้ในกฎหมายที่เกี่ยวข้อง เพื่อป้องกันการเข้าใช้ ปลอม
                ดัดแปลง แก้ไข เปิดเผย หรือกระทำการอื่นใดเกี่ยวกับข้อมูลส่วนบุคคลของท่านโดย
                ไม่ได้รับอนุญาตหรือไม่ชอบด้วยกฎหมาย
                อย่างไรก็ดีมาตรการรักษาความปลอดภัยข้อมูลส่วนบุคคลดังกล่าว อาจมีข้อบกพร่อง
                หรือทำงานผิดพลาดทำให้ข้อมูลรั่วไหล
                เนื่องจากการทำงานของระบบที่อาจจะเกิดจากการถูกไวรัส
                หรือโดนบุคคลภายนอกเจาะระบบฐานข้อมูลที่ไม่อาจควบคุมได้เป็นครั้งคราว
                ซึ่งบริษัทได้ใช้ความระมัดระวังและป้องกัน อย่างดีที่สุดแล้ว ฉะนั้นบริษัท วีคเดย์
                จำกัดไม่รับประกันหรือรับรองประสิทธิภาพของมาตรการที่ใช้รักษาความปลอดภัย
                ข้อมูลส่วนบุคคลของท่านดังกล่าวข้างต้น
                <br />
                <br />
                <Span $bold>6. สิทธิความคุ้มครองข้อมูลส่วนบุคคล</Span>
                <br />
                บริษัท วีคเดย์ จำกัด ขอแจ้งให้ท่านทราบว่า
                ท่านมีสิทธิที่จะขอดำเนินการแก้ไขข้อมูลส่วนตัวของท่านให้ถูกต้องสมบูรณ์
                เพื่อให้เป็นไปตามความถูกต้องแท้จริงตามข้อมูลปัจจุบัน
                หรือใช้สิทธิอื่นใดภายใต้ความคุ้มครองทางกฎหมาย เพื่อขอแก้ไขได้
                โดยท่านสามารถใช้สิทธินี้โดยแสดงความจำนงค์ตามช่องทางการติดต่อด้านล่างนี้
                <br />
                <br />
                <Span $bold>7. ช่องทางการติดต่อเรา </Span>
                <br />
                ท่านสามารถติดต่อเพื่อใช้สิทธิความคุ้มครองข้อมูลส่วนบุคคล
                โดยจัดส่งเป็นหนังสือพร้อมแนบสำเนาหลักฐานแสดงตัวตนของท่าน ตามที่อยู่ดังนี้ [*****]
                <br />
                <br />
                <Span $bold>8. การเปลี่ยนแปลงนโยบายคุ้มครองข้อมูลส่วนบุคคล</Span>
                <br />
                บริษัท วีคเดย์ จำกัด
                อาจขอแก้ไขเปลี่ยนแปลงนโยบายคุ้มครองข้อมูลส่วนบุคคลของท่านป็นครั้งคราวโดยไม่ได้แจ้งให้
                ท่านทราบล่วงหน้า เนื่องจากเป็นการแก้ไขให้ถูกต้องตามข้อมูลปัจจุบันที่แท้จริง ดังนั้น
                เพื่อประโยชน์ของท่านเอง บริษัท วีคเดย์ จำกัด จะดำเนินการแก้ไขข้อมูลส่วนตัวของท่าน
                เพื่อให้สอดคล้องกับการให้บริการและการดำเนินงาน ให้ถูกต้อง ทั้งนี้
                การแก้ไขนโยบายคุ้มครองข้อมูลส่วนบุคคลมีผลใช้บังคับทันทีที่มีการประกาศใช้
                ซึ่งจะนับตั้งแต่วันที่ดังระบุไว้ด้านล่าง ดังนั้นบริษัท วีคเดย์ จำกัด
                จึงขอแนะนำให้ท่านอ่านนโยบายคุ้มครองข้อมูลส่วนบุคคลทุกครั้งที่เข้าเยี่ยมชมหรือเข้าใช้เว็บไซต์หรือแอพพลิเคชั่นที่บริษัท
                วีคเดย์ จำกัด ให้บริการ เพื่อประโยชน์สูงสุดแก่ตัวท่านเอง
                หรือบุคคลภายนอกที่เกี่ยวข้อง นโยบายความเป็นส่วนตัวนี้ จัดทำหรือแก้ไข
                และมีผลตั้งแต่วันที่ [*****]
            </Text>
            <CheckboxGroup component="fieldset" style={{ margin: '40px 0' }}>
                <FormGroup row>
                    <Checkbox
                        label={
                            <div style={{ marginLeft: '10px' }}>
                                ฉันยอมรับนโยบายคุ้มครองข้อมูลส่วนบุคคลของบริษัท วีคเดย์ จำกัด
                            </div>
                        }
                        checked={accepted}
                        name="accepted"
                        onChange={onCheckChange}
                    />
                </FormGroup>
            </CheckboxGroup>
            <div style={{ display: 'flex', justifyContent: 'center', columnGap: '10px' }}>
                <OutlinedButton $width="120" $height="32px" onClick={onClose}>
                    Decline
                </OutlinedButton>
                <ContainedButton
                    $width="120"
                    $height="32px"
                    disabled={!accepted}
                    onClick={onAccept}
                >
                    Accept
                </ContainedButton>
            </div>
        </StyledDialog>
    );
};

export const ConfirmDeleteActivityDialog = ({ open, id, onCancel, onConfirm, ...rest }) => {
    return (
        <StyledDialog
            {...rest}
            open={open}
            onClose={onCancel}
            aria-labelledby={`activity-delete-dialog`}
            PaperComponent={StyledPaper}
        >
            <Text $fontSize="16px" $medium style={{ marginBottom: '10px' }}>
                Are you sure you want to delete this activity?
            </Text>
            <StyledDialogContent>
                <Text $fontSize="14px" $color="gray2">
                    Please confirm if you want to proceed with the deletion.
                </Text>
            </StyledDialogContent>

            <StyledDialogActions>
                <OutlinedButton onClick={onCancel} $width="120px" $height="32px" $color="pink">
                    Cencel
                </OutlinedButton>

                <ContainedButton onClick={onConfirm} $width="139px" $height="32px">
                    Yes, Delete
                </ContainedButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export const PermissionDialog = ({ open, onClose }) => {
    let src;

    if (isSafari) {
        if (isMobile) {
            src = safari_mobile;
        } else {
            src = safari;
        }
    } else if (isFirefox) {
        src = firefox;
    } else if (isEdge) {
        src = edge;
    } else {
        if (isMobile) {
            src = chrome_mobile;
        } else {
            src = chrome;
        }
    }

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            aria-labelledby={`skip-queue-dialog`}
            aria-describedby="dialog will be visible when queue was skip or provider hung up before client answer"
            PaperComponent={StyledPaperPermission}
            PaperProps={{
                style: {
                    maxWidth: '330px',
                },
            }}
        >
            <StyledDialogContent
                style={{
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    rowGap: '20px',
                }}
            >
                <div className="w-full relative">
                    <div
                        style={{
                            position: 'absolute',
                            top: 10,
                            right: 20,
                            color: '#B7B9B8',
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                        onClick={() => onClose()}
                    >
                        <div style={{ width: 13, marginRight: 5, marginTop: 3 }}>
                            <ClosePermission />
                        </div>
                        ปิด
                    </div>
                    <img width={'100%'} src={src} />
                </div>
            </StyledDialogContent>
        </StyledDialog>
    );
};

HungUpDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
};
